import React from 'react';
import { useDropzone } from 'react-dropzone';
import { FiUploadCloud, FiFile, FiX } from 'react-icons/fi';
import useImageUpload from "../utils/UploadImage";
import JSZip from 'jszip';
import { CLASS_COLORS } from "../utils/classColors";
import {
  saveProjectClasses,
  saveAllAnnotations,
  saveProjectSegmentAnnotations,
  setAnnotationType,
  saveAllOcrAnnotations
} from "../utils/crudFunctions";
import {


} from "../utils/ImageDB";

const UploadDataset = ({ projectInput, userName }) => {
  const [file, setFile] = React.useState(null);
  const [processingStage, setProcessingStage] = React.useState(null);
  const [processingProgress, setProcessingProgress] = React.useState({
    current: 0,
    total: 0
  });

  const { handleUpload, uploadProgress, retryFailedBatches, isLoading } = useImageUpload({
    projectInput,
    userName,
    setProjectName: () => { },
    setNameConfirmed: () => { },
  });


  // Helper to get image dimensions from blob
  const getImageDimensions = (blob) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
        });
      };
      img.src = URL.createObjectURL(blob);
    });
  };

  const processDataset = async (zipFile) => {
    try {
  
      setProcessingStage('initializing');
      console.log("[Debug] Starting dataset processing...");
      const zip = new JSZip();
      const content = await zip.loadAsync(zipFile);

      // Validate ZIP structure
      setProcessingStage('validating');
      const hasImagesFolder = content.folder("images") !== null;
      const hasLabelsFolder = content.folder("labels") !== null;
      const hasRootLabelsTxt = content.file("labels.txt") !== null;
      const hasLabelsTxtInFolder = content.folder("labels") && content.folder("labels").file("labels.txt") !== null;
      const isOcr = (hasRootLabelsTxt || hasLabelsTxtInFolder) && !content.file("classes.txt");

      if (!hasImagesFolder) {
        throw new Error(
          "Invalid dataset structure. ZIP file must contain an images/ folder."
        );
      }

      if (isOcr) {
        // OCR DATASET HANDLING
        setProcessingStage('processing_ocr');
        // Find the labels.txt file (either in root or labels/)
        let labelsFile = null;
        if (hasRootLabelsTxt) {
          labelsFile = content.file("labels.txt");
        } else if (hasLabelsTxtInFolder) {
          labelsFile = content.folder("labels").file("labels.txt");
        }
        if (!labelsFile) {
          throw new Error("OCR dataset must have labels.txt in root or labels/ folder.");
        }
        // Process images
        const imagesFolder = content.folder("images");
        const imageFiles = Object.values(imagesFolder.files).filter(
          file => !file.dir && file.name.match(/\.(jpg|jpeg|png|gif|bmp)$/i)
        );
        setProcessingProgress({ current: 0, total: imageFiles.length });
        // Parse OCR labels.txt (assume format: image_name<TAB>text per line)
        const labelsContent = await labelsFile.async("text");
        const ocrAnnotations = labelsContent.split("\n").filter(Boolean).map((line, idx) => {
          const [imageName, ...textParts] = line.split("\t");
          let text = textParts.join("\t").trim();
          let annotations = [];
          try {
            const arr = JSON.parse(text);
            if (Array.isArray(arr)) {
              annotations = arr.map((ann, i) => ({
                points: ann.points.flat(),
                id: Date.now().toString() + i,
                class: ann.transcription
              }));
            }
          } catch (e) {
            // fallback: keep as string if not valid JSON
          }
          return {
            imageName: imageName.trim(),
            imageIndex: idx,
            annotations
          };
        });
        // Prepare images for upload
        const images = [];
        for (let i = 0; i < imageFiles.length; i++) {
          const file = imageFiles[i];
          setProcessingProgress({ current: i + 1, total: imageFiles.length });
          const imageBlob = await file.async("blob");
          const imageName = file.name.split('/').pop();
          images.push({
            name: imageName,
            blob: new File([imageBlob], imageName, { type: imageBlob.type }),
            id: i
          });
        }
        // Upload images
        setProcessingStage('uploading_images');
        await handleUpload(images);
        // Save OCR annotations
        setProcessingStage('saving_annotations');
        console.log("[Debug] Saving OCR annotations:", ocrAnnotations);
        await saveAllOcrAnnotations(ocrAnnotations, projectInput);
        await setAnnotationType(projectInput, 'ocr');
        setProcessingStage('complete');
        return { success: true };
      }

      // Validate ZIP structure for non-OCR (YOLO/segment)
      const hasClassesFile = content.file("classes.txt") !== null;
      if (!hasLabelsFolder || !hasClassesFile) {
        throw new Error(
          "Invalid dataset structure. ZIP file must contain:\n" +
          "- images/ folder\n" +
          "- labels/ folder\n" +
          "- classes.txt file"
        );
      }

      // Parse classes.txt
      setProcessingStage('parsing_classes');
      const classes = await parseClassesFile(content.file("classes.txt"));

      // Process images and annotations
      setProcessingStage('processing_images');
      const { images, allAnnotations, hasSegments } = await processImagesAndLabels(
        content.folder("images"),
        content.folder("labels"),
        classes
      );
      console.log("[Debug] Processed data:", {
        imageCount: images.length,
        annotationCount: allAnnotations.length,
        hasSegments
      });

      // Save classes
      if (classes.length > 0) {
        setProcessingStage('saving_classes');
        // await saveClasses(classes, projectInput);
        await saveProjectClasses(classes, projectInput);
      }

      // Upload images using the hook's handleUpload
      setProcessingStage('uploading_images');
      await handleUpload(images);

      // Save annotations based on type
      setProcessingStage('saving_annotations');
      await saveAnnotationsToDb(allAnnotations, hasSegments);

      // Set annotation type
      await setAnnotationType(projectInput, hasSegments ? 'segment' : 'label');

      setProcessingStage('complete');
      return { success: true };
    } catch (error) {
      setProcessingStage('error');
      throw error;
    } finally {
      setTimeout(() => {
        setProcessingStage(null);
      }, 2000);
    }
  };

  const parseClassesFile = async (classesFile) => {
    const classesContent = await classesFile.async("text");
    const classes = classesContent
      .split("\n")
      .filter((line) => line.trim())
      .map((line, index) => {
        const [id, name] = line.split(" ");
        const classObj = {
          index: parseInt(id),
          id: name,
          name: name,
          color: CLASS_COLORS[index % CLASS_COLORS.length],
        };
        return classObj;
      });
    return classes;
  };

  const processImagesAndLabels = async (imagesFolder, labelsFolder, classes) => {
    const images = [];
    const allAnnotations = [];
    let hasAnySegmentation = false;

    const imageFiles = Object.values(imagesFolder.files).filter(
      file => !file.dir && file.name.match(/\.(jpg|jpeg|png|gif|bmp)$/i)
    );

    setProcessingProgress({ current: 0, total: imageFiles.length });

    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];
      setProcessingProgress({ current: i + 1, total: imageFiles.length });

      try {
        const imageBlob = await file.async("blob");
        // Remove 'images/' prefix from name
        const imageName = file.name.split('/').pop();
        const imageIndex = images.length;

        images.push({
          name: imageName,
          blob: new File([imageBlob], imageName, { type: imageBlob.type }),
          id: imageIndex
        });

        // Get base name for label file, no 'labels/' prefix needed
        const labelFileName = imageName.replace(/\.[^/.]+$/, ".txt");
        const labelFile = labelsFolder?.file(labelFileName);

        if (labelFile) {
          const dimensions = await getImageDimensions(imageBlob);
          const { annotations, hasSegments } = await processLabelFile(
            labelFile,
            imageIndex,
            imageName,
            classes,
            dimensions
          );
          hasAnySegmentation = hasAnySegmentation || hasSegments;
          allAnnotations.push({
            imageIndex,
            imageName,
            [hasSegments ? 'segments' : 'annotations']: annotations,
          });
        } else {
          console.log("[Debug] No label file found for:", labelFileName);
        }
      } catch (error) {
        console.error("[Debug] Error processing image:", file.name, error);
      }
    }

    console.log("[Debug] Finished processing all images and labels:", {
      totalImages: images.length,
      totalAnnotations: allAnnotations.length,
      hasSegmentation: hasAnySegmentation
    });

    return { images, allAnnotations, hasSegments: hasAnySegmentation };
  };

  const processLabelFile = async (labelFile, imageIndex, imageName, classes, dimensions) => {
    const labelContent = await labelFile.async("text");
    const annotations = labelContent
      .split("\n")
      .filter((line) => line.trim() && line !== "null")
      .map((line, index) => {
        const values = line.split(" ").map(Number);
        const classIndex = values[0];
        const classObj = classes.find((c) => c.index === classIndex) || {
          id: classIndex.toString(),
          name: classIndex.toString(),
          color: CLASS_COLORS[classIndex % CLASS_COLORS.length],
          index: classIndex
        };

        if (values.length > 5) {
          // Segmentation annotation
          const points = [];
          for (let i = 1; i < values.length; i += 2) {
            points.push([values[i], values[i + 1]]);
          }
          const annotation = {
            classIndex,
            id: `dataset_${imageIndex}_${index}`,
            label: {
              color: classObj.color,
              id: classObj.id,
              index: classObj.index,
              name: classObj.name
            },
            points
          };
          return annotation;
        } else {
          // Bounding box annotation
          const [, xCenter, yCenter, width, height] = values;
          const absWidth = width * dimensions.width;
          const absHeight = height * dimensions.height;
          const absX = xCenter * dimensions.width - absWidth / 2;
          const absY = yCenter * dimensions.height - absHeight / 2;

          const annotation = {
            classIndex,
            id: `dataset_${imageIndex}_${index}`,
            class: classObj,
            x: absX,
            y: absY,
            width: absWidth,
            height: absHeight
          };
          return annotation;
        }
      });

    const result = {
      annotations,
      hasSegments: annotations.some(ann => 'points' in ann)
    };
    console.log("[Debug] Finished processing label file:", result);
    return result;
  };

  const saveAnnotationsToDb = async (allAnnotations, hasSegments) => {
    if (hasSegments) {
      // await saveSegmentAnnotations(allAnnotations);
      await saveProjectSegmentAnnotations(allAnnotations, projectInput);
    } else {
      // await saveAnnotations(allAnnotations);
      await saveAllAnnotations(allAnnotations, projectInput);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/zip': ['.zip']
    },
    multiple: false,
    maxFiles: 1,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
        try {
          await processDataset(acceptedFiles[0]);
        } catch (error) {
          console.error("Error processing dataset:", error);
          alert(`Error processing dataset: ${error.message}`);
        }
      }
    }
  });

  const removeFile = () => {
    setFile(null);
    setProcessingStage(null);
  };

  const getStageMessage = () => {
    switch (processingStage) {
      case 'initializing':
        return "Initializing dataset processing...";
      case 'validating':
        return "Validating dataset structure...";
      case 'parsing_classes':
        return "Parsing class definitions...";
      case 'processing_images':
        return `Processing images and labels (${processingProgress.current}/${processingProgress.total})...`;
      case 'saving_classes':
        return "Saving class definitions to database...";
      case 'uploading_images':
        return "Uploading images to storage...";
      case 'saving_annotations':
        return "Saving annotations to database...";
      case 'complete':
        return "Dataset import completed successfully!";
      case 'error':
        return "Error occurred during processing";
      case 'processing_ocr':
        return "Processing OCR dataset...";
      default:
        return null;
    }
  };

  return (
    <div className="max-w-md mx-auto p-6">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragActive
          ? "border-blue-500 bg-blue-50"
          : "border-gray-300 hover:border-blue-400"
          } ${processingStage ? 'opacity-50 pointer-events-none' : ''
          }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center space-y-4">
          <FiUploadCloud className="w-12 h-12 text-gray-400" />
          {isDragActive ? (
            <p className="text-blue-500 font-medium">Drop the dataset ZIP file here</p>
          ) : (
            <>
              <p className="font-medium text-gray-700">
                Drag & drop a YOLO format dataset ZIP file here
              </p>
              <p className="text-sm text-gray-500">
                Should contain images/ and labels/ folders with classes.txt
              </p>
            </>
          )}
        </div>
      </div>

      {/* Processing Status Section */}
      {processingStage && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-700">
                {getStageMessage()}
              </div>
              {processingStage === 'complete' ? (
                <span className="text-green-500">✓</span>
              ) : processingStage === 'error' ? (
                <span className="text-red-500">✗</span>
              ) : (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              )}
            </div>

            {(processingStage === 'processing_images' || processingStage === 'uploading_images') && (
              <div className="relative pt-1">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium text-gray-500">
                    Progress: {processingProgress.current} of {processingProgress.total}
                  </div>
                  <div className="text-xs font-medium text-blue-600">
                    {Math.round((processingProgress.current / processingProgress.total) * 100)}%
                  </div>
                </div>
                <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-100">
                  <div
                    style={{ width: `${(processingProgress.current / processingProgress.total) * 100}%` }}
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-300"
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Progress Section */}
      {uploadProgress.dummyProgress > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="space-y-3">
            <div className="relative pt-1">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-gray-700">Upload Progress</div>
                <div className="text-sm font-medium text-blue-600">{uploadProgress.dummyProgress.toFixed(1)}%</div>
              </div>
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-100">
                <div
                  style={{ width: `${uploadProgress.dummyProgress}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-300"
                ></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Speed:</span> {uploadProgress.speed} MB/s
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">ETA:</span> {uploadProgress.eta}
              </div>
            </div>
            {uploadProgress.failedBatches.length > 0 && (
              <button
                onClick={() => retryFailedBatches(uploadProgress.failedBatches)}
                className="w-full mt-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-md hover:bg-yellow-200 transition-colors duration-200 font-medium text-sm"
              >
                Retry Failed Batches
              </button>
            )}
          </div>
        </div>
      )}

      {file && !isLoading && !processingStage && uploadProgress.dummyProgress === 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFile className="text-gray-500" />
              <span className="text-sm font-medium text-gray-700 truncate max-w-xs">
                {file.name}
              </span>
            </div>
            <button
              onClick={removeFile}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Remove file"
            >
              <FiX />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadDataset;